import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "wouter";
import { SOCIAL_ICONS } from "@/lib/socialLinks";
import { openReceptionist } from "@/vault-template/components/ReceptionistWidget";
import GhostWatermark from "@/vault-template/components/GhostWatermark";
import type { FooterModel } from "@shared/footerModel";
import { deriveHeroFooterTheme } from "@shared/heroFooterTheme";
import { DEFAULT_DARK_ACCENT } from "@shared/accentDerivation";

// Mirrors the root-served sales page footer (the vault Contact footer in
// sales mode) EXACTLY: same dark themed band, watermark (faded logo or ghost
// word), and a single tier — Sign Up | Sign In on the left with the social
// icons and copyright stacked under them, the legal navigation
// (Contact | Terms | Privacy) right-aligned on
// the same row. The footer element carries the `vault-template-root` class so
// the scoped vault CSS (notably the .font-serif → display-face mapping the
// ghost word relies on) applies here exactly as it does on the sales page.
// LIVE DATA: it fetches the sales owner's shared footer view-model from
// /api/sales-footer (the SAME buildFooterModel the "/" sales page and legal
// pages render from) plus the owner's hero/footer background color, so
// editing the live website footer updates these marketing pages too. When
// the endpoint returns null (dev without admin data) or fails, a static
// InControl-branded version of the SAME layout renders instead.

// Template defaults — the same fallbacks the vault template CSS uses when
// the owner hasn't picked a custom hero/footer color (vault-template.css).
const DEFAULT_FOOTER_BG = "#000000";
const DEFAULT_FOOTER_MUTED = "#EAEAEA";
const DEFAULT_LIGHT = "#FFFFFF";

type SalesFooterModel = FooterModel & {
  heroFooterBgColor?: string;
  footerWord?: string;
  // Footer watermark mode ("logo" default) + the primary logo URL — so this
  // footer can mirror the root sales footer's faded-logo watermark exactly
  // (the wide logo rides along on the shared FooterModel).
  footerBrandMode?: "none" | "logo" | "word";
  footerBrandOpacity?: number;
  footerTaglineOpacity?: number;
  footerLogoUrl?: string;
  logoUrl?: string;
  // Owner tagline rendered as the small watermark-styled line under the
  // faded logo / ghost word (mirrors the vault footer). "" = none.
  tagline?: string;
  // Dark-section accent for the thin accent divider across the footer's top
  // edge (mirrors the vault footer, Contact.tsx). "" → template default.
  accentColorDark?: string;
};

// Module-level cache so navigating between marketing pages doesn't refetch
// (and never flashes the fallback after the first successful load).
let cachedModel: SalesFooterModel | null | undefined;

export default function Footer() {
  const [model, setModel] = useState<SalesFooterModel | null>(cachedModel ?? null);

  useEffect(() => {
    if (cachedModel !== undefined) return;
    let alive = true;
    fetch("/api/sales-footer")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: SalesFooterModel | null) => {
        cachedModel = data;
        if (alive && data) setModel(data);
      })
      .catch(() => {
        cachedModel = null;
      });
    return () => {
      alive = false;
    };
  }, []);

  const businessName = model?.businessName || "InControl";
  const socials = model?.socials ?? [];

  // Same derived theme as the root footer: the owner's picked hero/footer
  // background drives the band color + muted text; unset keeps the template
  // default dark design.
  const hf = deriveHeroFooterTheme(model?.heroFooterBgColor);
  const footerBg = hf?.bg ?? DEFAULT_FOOTER_BG;
  const footerFg = hf?.mutedText ?? DEFAULT_FOOTER_MUTED;
  const lightBg = hf ? hf.text : DEFAULT_LIGHT;

  const Divider = () => (
    <span
      aria-hidden
      className="hidden sm:inline select-none"
      style={{ color: "color-mix(in srgb, var(--footer-fg) 10%, transparent)" }}
    >
      |
    </span>
  );

  const hoverIn = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = "var(--theme-light-bg)";
  };
  const hoverOut = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = "var(--footer-fg)";
  };
  const linkClass = "text-[13px] whitespace-nowrap transition-colors";
  const linkStyle: CSSProperties = { color: "var(--footer-fg)" };

  // Social icon buttons — Tier 1 (right side) normally, merged into the
  // Tier-2 links row when phone + address are both absent (Tier 1 skipped).
  const socialIcons =
    socials.length > 0 ? (
      <div className="flex items-center gap-2" data-testid="social-links">
        {socials.map(({ href, label, key }) => {
          const Icon = SOCIAL_ICONS[key];
          return (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="w-8 h-8 rounded-md flex items-center justify-center transition-all duration-200"
              style={{
                border: "2px solid color-mix(in srgb, var(--theme-light-bg) 55%, transparent)",
                color: "var(--footer-fg)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--theme-light-bg)";
                e.currentTarget.style.borderColor = "var(--theme-light-bg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--footer-fg)";
                e.currentTarget.style.borderColor =
                  "color-mix(in srgb, var(--theme-light-bg) 55%, transparent)";
              }}
              data-testid={`link-social-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-3.5 h-3.5" />
            </a>
          );
        })}
      </div>
    ) : null;

  // Booking CTA — accent pill at the end of the socials row, mirroring the
  // vault footer (Contact.tsx). model.bookingHref is "" when unset/off.
  // "schedule" kind navigates same-tab to the class-schedule anchor;
  // "external" kind opens the owner's booking URL in a new tab.
  const isExternalBooking = model?.bookingKind === "external";
  const bookingButton = model?.bookingHref ? (
    <a
      href={model.bookingHref}
      target={isExternalBooking ? "_blank" : undefined}
      rel={isExternalBooking ? "noopener noreferrer" : undefined}
      className="inline-flex items-center whitespace-nowrap rounded-md px-5 py-2.5 text-[15px] font-semibold leading-none transition-opacity hover:opacity-90 active:scale-[0.99]"
      style={{ background: model?.accentColorDark || DEFAULT_DARK_ACCENT, color: "#FFFFFF" }}
      data-testid="link-footer-booking"
    >
      {model.bookingLabel || "Schedule Here"}
      <span aria-hidden="true" className="ml-2 inline-flex items-center">
        <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
          <path d="M2 6h8M6.5 2.5 10 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </a>
  ) : null;
  // Copyright sits directly under the socials, before the booking button.
  // Keep in lockstep with the
  // vault footer (Contact.tsx).
  const socialsRow =
    (
      <div className="flex flex-col items-center gap-3 sm:items-start" data-testid="sales-footer-socials">
        {socialIcons}
        <span className="text-[13px] whitespace-nowrap" style={linkStyle} data-testid="text-copyright">
          {model?.copyright || "Copyright 2026 All Rights Reserved"}
        </span>
        {bookingButton}
      </div>
    );

  return (
    <footer
      className="vault-template-root relative overflow-x-clip mt-12"
      style={
        {
          backgroundColor: footerBg,
          color: footerFg,
          // The vars the ghost watermark + hover styles read (the same names
          // the vault template sets, so the pieces render identically here).
          ["--footer-fg" as string]: footerFg,
          ["--theme-cream-bg" as string]: footerBg,
          ["--theme-footer-muted" as string]: footerFg,
          ["--theme-light-bg" as string]: lightBg,
        } as CSSProperties
      }
      data-testid="section-footer"
    >
      {/* Thin accent divider across the top of the footer — the twin of the
          vault footer's bar (Contact.tsx); keep the two in lockstep. Uses the
          owner's dark-section accent, template default until data resolves. */}
      <div
        className="relative z-10 h-2 w-full"
        style={{ background: model?.accentColorDark || DEFAULT_DARK_ACCENT }}
        aria-hidden="true"
        data-testid="footer-accent-bar"
      />
      {/* Footer watermark — identical to the root sales footer (Contact.tsx):
          owner-chosen mode, "logo" (default) shows the faded logo, "word" the
          ghost word. Only rendered once live data resolves. */}
      {model?.footerBrandMode === "none" ? null : model && model.footerBrandMode !== "word" ? (
        <GhostWatermark
          name=""
          logoUrls={[model.footerLogoUrl || "", model.wideLogoUrl || "", model.logoUrl || ""]}
          tagline={model.tagline || ""}
          brandOpacity={model.footerBrandOpacity}
          taglineOpacity={model.footerTaglineOpacity}
        />
      ) : (
        <GhostWatermark
          name={model ? (model.footerWord || "").trim() || businessName : ""}
          tagline={model?.tagline || ""}
          brandOpacity={model?.footerBrandOpacity}
          taglineOpacity={model?.footerTaglineOpacity}
        />
      )}
      {/* min-h + centered content — mirrors the vault footer (Contact.tsx):
          the taller floor lets the watermark render at a readable size while
          the footer content stays vertically centered. */}
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 relative flex min-h-[240px] flex-col justify-center">
        <div className="pt-8 pb-[5.5rem]" data-testid="footer-content-padding">
          <div className="flex flex-col gap-6">
            {/* Single tier — mirrors the root sales footer (Contact.tsx
                salesMode): Sign Up | Sign In left with the socials stacked
                under them, the legal block (Contact | Terms | Privacy +
                copyright + attribution) right-aligned on the same row. */}
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col items-center gap-1 text-center sm:items-start sm:text-left">
                <span className="flex items-center gap-2 text-[14px] font-semibold whitespace-nowrap">
                  <a
                    href="https://useincontrol.com"
                    className="transition-colors"
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="link-sales-home"
                  >
                    Home
                  </a>
                  <span aria-hidden="true" style={{ color: "color-mix(in srgb, var(--footer-fg) 40%, transparent)" }}>|</span>
                  <a
                    href="https://useincontrol.com/start/monthly"
                    className="transition-colors"
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="link-sales-signup"
                  >
                    Sign Up
                  </a>
                  <span aria-hidden="true" style={{ color: "color-mix(in srgb, var(--footer-fg) 40%, transparent)" }}>|</span>
                  <Link
                    href="/sign-in"
                    className="transition-colors"
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="link-sales-login"
                  >
                    Sign In
                  </Link>
                </span>
                {/* Phone line (Aug 2026, owner request) — mirrors the vault
                    footer's phone rendering (Contact.tsx) exactly; the shared
                    model already carries phone/callHref/textHref. Keep in
                    lockstep with Contact.tsx (sales-footer-mirror). */}
                {model?.phone && (
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    {model.callHref && model.textHref ? (
                      <>
                        <span className="text-[14px]" style={linkStyle} data-testid="text-phone">
                          {model.phone}
                        </span>
                        <a
                          href={model.callHref}
                          className="text-[13px] font-semibold underline underline-offset-2 transition-colors"
                          style={linkStyle}
                          onMouseEnter={hoverIn}
                          onMouseLeave={hoverOut}
                          data-testid="link-phone-call"
                        >
                          Call
                        </a>
                        <span style={{ color: "color-mix(in srgb, var(--footer-fg) 40%, transparent)" }}>|</span>
                        <a
                          href={model.textHref}
                          className="text-[13px] font-semibold underline underline-offset-2 transition-colors"
                          style={linkStyle}
                          onMouseEnter={hoverIn}
                          onMouseLeave={hoverOut}
                          data-testid="link-phone-text"
                        >
                          Text
                        </a>
                      </>
                    ) : model.phoneHref ? (
                      <a
                        href={model.phoneHref}
                        className="text-[14px] whitespace-nowrap transition-colors"
                        style={linkStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                        data-testid="link-phone"
                      >
                        {model.phone}
                      </a>
                    ) : (
                      <span className="text-[14px]" style={linkStyle} data-testid="text-phone">
                        {model.phone}
                      </span>
                    )}
                  </span>
                )}
                {socialsRow && <div className="mt-2">{socialsRow}</div>}
              </div>

              {/* Marketing footer navigation stays on the right; copyright
                  belongs to the social column, with no self-attribution. */}
              <div className="flex flex-col items-center gap-y-2 sm:items-end">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:justify-end">
                  {/* "Contact" opens the Digital Receptionist chat widget —
                      every page that renders this footer also mounts
                      SalesReceptionist (the same bubble as the root page). */}
                  <button
                    type="button"
                    onClick={() => openReceptionist()}
                    className={linkClass}
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="button-contact-us"
                  >
                    Contact
                  </button>
                  <Divider />
                  <Link
                    href="/terms"
                    className={linkClass}
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="link-footer-terms"
                  >
                    Terms
                  </Link>
                  <Divider />
                  <Link
                    href="/privacy"
                    className={linkClass}
                    style={linkStyle}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                    data-testid="link-footer-privacy"
                  >
                    Privacy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
